import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LangContext";
import { ImageIcon } from "lucide-react";

const DARK_GRAD = "linear-gradient(150deg, #0d2137 0%, #0a0f1e 55%, #060c18 100%)";

export default function Nosotros() {
  const { tr } = useLang();

  return (
    <div className="bg-background text-foreground overflow-x-hidden selection:bg-primary/30">

      {/* Hero */}
      <section className="relative pt-40 pb-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="/assets/hero-abstract.png" alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0" style={{ background: DARK_GRAD }} />
          <div className="absolute inset-0 dark-texture" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter mb-8 text-white">{tr.about.title}</h1>
            <p className="text-xl md:text-2xl text-slate-300 font-light leading-relaxed max-w-2xl mx-auto">{tr.about.desc}</p>
          </motion.div>
        </div>
      </section>

      {/* Historia — Text + Image (Light) */}
      <section className="py-32 px-6 bg-slate-50 border-y border-slate-200 text-slate-900">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">{tr.about.historyTitle}</h2>
            <p className="text-xl text-slate-600 font-light leading-relaxed mb-8">{tr.about.historyDesc}</p>

            <div className="grid grid-cols-2 gap-4">
              {tr.about.historyMilestones.map((milestone, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm"
                >
                  <span className="text-2xl font-black" style={{ color: "#38bdf8" }}>{milestone.year}</span>
                  <p className="text-slate-600 text-sm font-light leading-relaxed mt-2">{milestone.text}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl border border-slate-200"
          >
            <img src="/assets/nosotros.png" alt="Nuestra Historia" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Mission (Dark + blue gradient) */}
      <section className="py-32 px-6 text-foreground relative overflow-hidden" style={{ background: DARK_GRAD }}>
        <div className="absolute inset-0 dark-texture pointer-events-none" />
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center relative z-10">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-4xl font-extrabold mb-6 tracking-tight text-white">{tr.about.missionTitle}</h2>
            <p className="text-xl text-muted-foreground font-light leading-relaxed">{tr.about.missionDesc}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="aspect-video rounded-3xl overflow-hidden shadow-2xl border border-border">
            <img src="/assets/slide.png" alt="Mission" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Equipo / Team Photos (Light) */}
      <section className="py-32 px-6 bg-white border-y border-slate-200 text-slate-900">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">{tr.about.teamTitle}</h2>
            <p className="text-xl text-slate-500 font-light">{tr.about.teamDesc}</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
    {/*    {[tr.about.teamPhoto1, tr.about.teamPhoto2].map((label, i) => ( */}
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 1 * 0.15 }}>
                <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border-2 border-dashed border-slate-300 bg-slate-100 group cursor-pointer" title="Equipo">
                  <img src="/assets/Cristobal.png" alt="Cristobal Bello" className="w-full h-full object-cover" />
                </div>
              </motion.div>
              <motion.div  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 2 * 0.15 }}>
                <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border-2 border-dashed border-slate-300 bg-slate-100 group cursor-pointer" title="Equipo">
                  <img src="/assets/Naiky.png" alt="Naiky R." className="w-full h-full object-cover" />
                </div>
              </motion.div>
        {/*    ))} */}
          </div>
        </div>
      </section>

      {/* Values (Dark + blue gradient) */}
      <section className="py-32 px-6 text-foreground relative overflow-hidden" style={{ background: DARK_GRAD }}>
        <div className="absolute inset-0 grid-texture pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white">{tr.about.valuesTitle}</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {tr.about.values.map((val, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="p-10 rounded-3xl bg-white/5 border border-primary/20 hover:border-primary/40 transition-colors">
                <h3 className="text-2xl font-bold mb-4 text-white">{val.title}</h3>
                <p className="text-muted-foreground font-light leading-relaxed">{val.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us (Light) */}
      <section className="py-32 px-6 bg-white border-t border-slate-200 text-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl font-extrabold mb-8 tracking-tight">{tr.about.whyChooseUs}</h2>
            <p className="text-xl text-slate-600 font-light leading-relaxed">{tr.about.whyChooseUsDesc}</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
